import { createContext, useContext, ReactNode } from "react";

/**
 * Context for providing a data transform function to useFetch.
 *
 * This allows apps (like webconfig) to automatically transform API responses
 * without modifying the shared components that use useFetch.
 *
 * Example use case: webconfig needs to normalize $type values from firmware API
 * responses to match the CoreApi namespace expected by TypeScript types.
 */

export type FetchTransformFn = <T>(data: T) => T;

interface FetchTransformContextValue {
    transform?: FetchTransformFn;
}

const FetchTransformContext = createContext<FetchTransformContextValue>({});

export function useFetchTransform(): FetchTransformFn | undefined {
    const context = useContext(FetchTransformContext);
    return context.transform;
}

interface FetchTransformProviderProps {
    transform: FetchTransformFn;
    children: ReactNode;
}

export function FetchTransformProvider({ transform, children }: FetchTransformProviderProps) {
    return <FetchTransformContext.Provider value={{ transform }}>{children}</FetchTransformContext.Provider>;
}
